import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import { currentIdentityScope, telegramUserId } from "./customer-profile-v2.js";

const DRAFT_KEY = "asiastyle.store-v2.np-checkout-draft.v1";

export type NpCheckoutDraftV2 = {
  selection: NovaPoshtaReferenceSelection | null;
  manualFallback: boolean;
  updated_at: string;
  identity_scope?: string;
  telegram_user_id?: string | null;
};

function scopedKey(): string {
  return `${DRAFT_KEY}.scope.${currentIdentityScope()}`;
}

function legacyScopedKey(): string {
  const uid = telegramUserId();
  return uid ? `${DRAFT_KEY}.tg.${uid}` : DRAFT_KEY;
}

export function readNpCheckoutDraft(): NpCheckoutDraftV2 | null {
  try {
    const raw =
      window.sessionStorage.getItem(scopedKey()) ??
      window.sessionStorage.getItem(legacyScopedKey()) ??
      window.sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NpCheckoutDraftV2> & {
      telegram_user_id?: string | number | null;
      identity_scope?: string | null;
    };
    if (!parsed || typeof parsed !== "object") return null;
    const currentTg = telegramUserId();
    const parsedTg = parsed.telegram_user_id == null ? null : String(parsed.telegram_user_id);
    if (currentTg && parsedTg && currentTg !== parsedTg) return null;
    const parsedScope = String(parsed.identity_scope ?? "").trim();
    if (parsedScope && parsedScope !== currentIdentityScope()) return null;
    return {
      selection: (parsed.selection as NovaPoshtaReferenceSelection | null) ?? null,
      manualFallback: parsed.manualFallback === true,
      updated_at: String(parsed.updated_at ?? ""),
      identity_scope: currentIdentityScope(),
      telegram_user_id: currentTg,
    };
  } catch {
    return null;
  }
}

export function writeNpCheckoutDraft(input: {
  selection: NovaPoshtaReferenceSelection | null;
  manualFallback: boolean;
}): void {
  try {
    const payload: NpCheckoutDraftV2 = {
      selection: input.selection,
      manualFallback: input.manualFallback,
      updated_at: new Date().toISOString(),
      identity_scope: currentIdentityScope(),
      telegram_user_id: telegramUserId(),
    };
    window.sessionStorage.setItem(scopedKey(), JSON.stringify(payload));
    // Drop legacy unscoped / tg-only keys so another identity cannot revive them.
    try {
      window.sessionStorage.removeItem(DRAFT_KEY);
      const legacy = legacyScopedKey();
      if (legacy !== scopedKey()) window.sessionStorage.removeItem(legacy);
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

export function clearNpCheckoutDraft(): void {
  try {
    window.sessionStorage.removeItem(scopedKey());
    window.sessionStorage.removeItem(legacyScopedKey());
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}
